import { Banner, TextStyle } from '@assembly-web/ui';
import { defineMessages, useIntl } from 'react-intl';

import { DoraFeedbackSection } from '../../dora/DoraFeedbackSection';
import { DoraLoadingLabel } from '../../dora/DoraLoadingLabel';
import { AnimateChat } from '../shared/dora/AnimateChat';
import { DoraMessageBubble } from '../shared/dora/DoraMessageBubble';
import { DoraStackedMessages } from '../shared/dora/DoraStackedMessages';

const messages = defineMessages({
  typingMessage: { defaultMessage: 'Typing', id: 'N2bqd9' },
  feedbackLabel: {
    defaultMessage: 'Was my response helpful?',
    id: '0sYPgY',
  },
  chartBannerTitle: {
    defaultMessage: '🔥 Hot tip: Finding this chart hard to read?',
    id: 'GhiF+0',
  },
  chartBannerSubtitle: {
    defaultMessage:
      'Try asking Dora for different colors, formatting, timeframe, or to only include the top or bottom 10 people/groups in the chart',
    id: '70LoiO',
  },
});

export function DoraChatReportAnswer(
  props: {
    htmlResponses: string[];
    isAnswerSeen: boolean;
    isLoading: boolean;
    shouldAnimate: boolean;
  } & (
    | {
        showFeedbackControl: true;
        rawResponse: string;
        promptId: string;
      }
    | {
        showFeedbackControl: false;
      }
  )
) {
  const {
    htmlResponses,
    isAnswerSeen,
    isLoading,
    shouldAnimate = false,
    showFeedbackControl,
  } = props;

  const { formatMessage } = useIntl();

  const chatMessages = htmlResponses.map((htmlResponse, index) => (
    <AnimateChat
      key={index}
      shouldAnimateOnMount={Boolean(
        shouldAnimate && index === htmlResponses.length - 1 && !isAnswerSeen
      )}
    >
      <DoraMessageBubble>
        {htmlResponse.includes('<img data-dora-chart="" ') ? (
          <div className="space-y-3">
            <TextStyle as="span" className="space-y-2" html={htmlResponse} />

            <Banner status="info" className="rounded-lg">
              <TextStyle
                className="text-primary-9"
                variant="xs-medium"
                html={formatMessage(messages.chartBannerTitle)}
              />
              <TextStyle
                className="text-primary-9"
                variant="xs-regular"
                html={formatMessage(messages.chartBannerSubtitle)}
              />
            </Banner>
          </div>
        ) : (
          <TextStyle as="span" className="space-y-2" html={htmlResponse} />
        )}
      </DoraMessageBubble>
    </AnimateChat>
  ));

  if (isLoading) {
    chatMessages.push(
      <AnimateChat
        shouldAnimateOnMount={Boolean(shouldAnimate && !isAnswerSeen)}
      >
        <DoraMessageBubble>
          <DoraLoadingLabel label={formatMessage(messages.typingMessage)} />
        </DoraMessageBubble>
      </AnimateChat>
    );
  }

  return (
    <DoraStackedMessages
      shouldAnimateAvatar={Boolean(shouldAnimate && htmlResponses.length === 1)}
    >
      {chatMessages}
      {/* TODO[Siva]: Find out why booleans aren't handled well by react/jsx-no-leaked-render */}
      {/* eslint-disable-next-line react/jsx-no-leaked-render */}
      {showFeedbackControl && (
        <div className="col-start-2 flex self-start">
          <DoraMessageBubble>
            <DoraFeedbackSection
              feedbackSource="doraChat"
              feedbackLabel={formatMessage(messages.feedbackLabel)}
              promptId={props.promptId}
              markdownResponse={props.rawResponse}
            />
          </DoraMessageBubble>
        </div>
      )}
    </DoraStackedMessages>
  );
}
